import { differenceInCalendarDays, parseISO, startOfDay } from "date-fns";
import { isEmpty, isUndefined, omitBy } from "lodash-es";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AnalyticsContextData, StandardEventProperties } from "./types";

interface AnalyticsContextStore {
  context: AnalyticsContextData;
  userContext: Pick<
    AnalyticsContextData,
    "isLoggedIn" | "entryPoint" | "gcUserId" | "email" | "phoneNumber"
  >;
  setContext: (data: Partial<AnalyticsContextData>) => void;
  setUserContext: (
    data: Pick<
      AnalyticsContextData,
      "isLoggedIn" | "entryPoint" | "gcUserId" | "email" | "phoneNumber"
    >,
  ) => void;
  clearContext: () => void;
  getStandardProperties: () => StandardEventProperties;
}

const isReactNativeWebView = (): boolean =>
  typeof window !== "undefined" &&
  !!(window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView;

const detectPlatform = (): string => {
  if (isReactNativeWebView()) return "app";
  if (typeof navigator === "undefined") return "unknown";
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    ? "msite"
    : "website";
};

const detectOS = (): string => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "macOS";
  if (/Win/.test(ua)) return "Windows";
  if (/Linux/.test(ua)) return "Linux";
  return "unknown";
};

const computeLengthOfStay = (
  checkInDate?: string,
  checkOutDate?: string,
): string | undefined => {
  if (!checkInDate || !checkOutDate) return undefined;
  try {
    const nights = differenceInCalendarDays(
      parseISO(checkOutDate),
      parseISO(checkInDate),
    );
    return nights > 0 ? String(nights) : undefined;
  } catch {
    return undefined;
  }
};

const resolveRoomTypeNames = (
  roomTypeCodes: string[] = [],
  roomTypeNamesByCode?: Map<string, string>,
): string[] | undefined =>
  isEmpty(roomTypeCodes)
    ? undefined
    : roomTypeCodes.map((code) => roomTypeNamesByCode?.get(code) ?? code);

const computeDaysToCheckIn = (checkInDate?: string): number | undefined => {
  if (!checkInDate) return undefined;
  try {
    return differenceInCalendarDays(
      parseISO(checkInDate),
      startOfDay(new Date()),
    );
  } catch {
    return undefined;
  }
};

export const useAnalyticsContext = create<AnalyticsContextStore>()(
  persist(
    (set, get) => ({
      context: {},
      userContext: {},

      setContext: (data) =>
        set((state) => ({ context: { ...state.context, ...data } })),

      setUserContext: (data) =>
        set(() => ({
          userContext: data,
        })),

      clearContext: () => set({ context: {}, userContext: {} }),

      getStandardProperties: (): StandardEventProperties => {
        const { roomTypeCodes, roomTypeNamesByCode, ...ctx } = get().context;
        const userCtx = get().userContext;
        return omitBy(
          {
            ...ctx,
            ...userCtx,
            roomType: resolveRoomTypeNames(roomTypeCodes, roomTypeNamesByCode),
            abwDays: computeDaysToCheckIn(ctx.checkInDate),
            lengthOfStay: computeLengthOfStay(
              ctx.checkInDate,
              ctx.checkOutDate,
            ),
            platform: detectPlatform(),
            os: detectOS(),
            timestamp: new Date().toISOString(),
          },
          isUndefined,
        ) as StandardEventProperties;
      },
    }),
    {
      name: "analytics-user-context",
      partialize: (state) => ({
        userContext: state.userContext,
      }),
    },
  ),
);
